import { useEffect } from 'react'

import { Box, FloraButton, Flex } from '@grupoboticario/flora-react'
import type { CSS } from '@grupoboticario/flora-react'

import { applyTextFormatting, isFormattingActive } from '@/utils/textFormatting/textFormatting'

import * as S from './RichTextEditor.styles'
import { RichTextEditorProps } from './RichTextEditor.types'

const FORMATTING_BUTTONS: Array<{ command: 'bold' | 'italic' | 'underline' | 'link'; label: string; style: CSS }> = [
  { command: 'bold', label: 'B', style: { fontWeight: 'bold' } },
  { command: 'italic', label: 'I', style: { fontStyle: 'italic', fontWeight: 'normal' } },
  {
    command: 'underline',
    label: 'U',
    style: { textDecoration: 'underline', fontWeight: 'normal' }
  },
  { command: 'link', label: '🔗', style: { fontWeight: 'normal' } }
]

const EDITABLE_AREA_STYLES = {
  borderTop: 'none',
  border: 'none',
  borderRadius: '0 0 4px 4px',
  outline: 'none',
  fontSize: '14px',
  lineHeight: '1.4',
  color: '$dark-light',
  backgroundColor: 'transparent',
  overflow: 'auto',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  '&:focus': {
    borderColor: '$medium-light-3',
    boxShadow: 'none'
  },
  '& b, & strong': {
    fontWeight: 'bold'
  },
  '& i, & em': {
    fontStyle: 'italic'
  },
  '& u': {
    textDecoration: 'underline'
  }
}

/** Sobrescreve cor do texto no modo somente leitura (tokens Flora). */
const EDITABLE_AREA_READ_ONLY: Pick<typeof EDITABLE_AREA_STYLES, 'color'> = {
  color: '$disabledContent'
}

const RichTextEditor = ({
  editorRef,
  onBlur,
  onInput,
  maxTextLength,
  isReadOnly = false,
  placeholder = '',
  minHeight = '120px',
  maxHeight,
  maxWidth,
  rightButton,
  customStyles = {},
  formattingCommands
}: RichTextEditorProps) => {
  const visibleButtons = formattingCommands
    ? FORMATTING_BUTTONS.filter(({ command }) => formattingCommands.includes(command))
    : FORMATTING_BUTTONS
  // Bloqueia entrada nativa ao atingir o limite de caracteres
  useEffect(() => {
    if (!maxTextLength || !editorRef.current) return
    const el = editorRef.current

    const handleBeforeInput = (e: InputEvent) => {
      if (e.inputType?.startsWith('insert')) {
        const currentLength = el.textContent?.length ?? 0
        const insertText = e.data ?? e.dataTransfer?.getData('text/plain') ?? ''
        if (currentLength + insertText.length > maxTextLength) {
          e.preventDefault()
        }
      }
    }

    el.addEventListener('beforeinput', handleBeforeInput)
    return () => el.removeEventListener('beforeinput', handleBeforeInput)
  }, [maxTextLength, editorRef])

  const applyFormatting = (command: 'bold' | 'italic' | 'underline' | 'link') => {
    if (!editorRef.current) return

    let options = {}
    if (command === 'link') {
      const isLinkActive = isFormattingActive('link')

      if (!isLinkActive) {
        const url = globalThis.prompt('Informe a URL do link:', 'https://')
        if (!url) return
        options = { url }
      }
    }

    const success = applyTextFormatting(command, editorRef.current, options)

    if (success) {
      setTimeout(() => {
        onBlur?.()
      }, 10)
    }
  }

  const editableBase = isReadOnly ? { ...EDITABLE_AREA_STYLES, ...EDITABLE_AREA_READ_ONLY } : EDITABLE_AREA_STYLES

  const editableAreaStyles = {
    ...editableBase,
    minHeight,
    ...(maxHeight && { maxHeight }),
    ...(maxWidth && { maxWidth }),
    marginBottom: '$1',
    padding: '$3',
    cursor: isReadOnly ? 'not-allowed' : 'text',
    '&:empty:before': {
      content: `"${placeholder}"`,
      color: '$medium-light-1',
      fontStyle: 'normal'
    },
    ...customStyles
  }

  const outerCss = isReadOnly ? S.outerBoxReadOnlyCss : S.outerBoxCss

  return (
    <Box css={outerCss}>
      <Flex justify="space-between" css={S.toolbarContainerCss}>
        <Flex css={S.toolbarCss}>
          {visibleButtons.map(({ command, label, style }) => (
            <FloraButton
              css={S.getFormattingButtonCss(style)}
              disabled={isReadOnly}
              hierarchy="tertiary"
              key={command}
              onClick={() => applyFormatting(command)}
              size="small"
              type="button"
            >
              {label}
            </FloraButton>
          ))}
        </Flex>
        {rightButton}
      </Flex>

      <Box
        contentEditable={!isReadOnly}
        css={editableAreaStyles}
        onBlur={onBlur}
        onInput={(e: React.FormEvent<HTMLDivElement>) => {
          onInput?.(e.currentTarget.textContent ?? '')
        }}
        ref={editorRef}
        suppressContentEditableWarning={true}
      />
    </Box>
  )
}

export default RichTextEditor
